import { Button, Collapse, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronUp, IconDeviceFloppy } from '@tabler/icons-react';
import { useState } from 'react';
import getErrorMessage from '../../helpers/getErrorMessage';
import { toBrowserStepsPayload, validateBrowserSteps } from '../../helpers/knowledgeIngest';
import useFetch from '../../helpers/useFetch';
import BrowserStepsEditor from './BrowserStepsEditor';

const HINTS = {
  domain:
    'Applied when this page is imported, after this store’s consent banner providers and client-wide browser steps (Knowledge base import, in the Super admin block below). Saving does not re-import: use Re-fetch source.',
  system:
    'Applied when this page is imported. The system corpus has no consent banner providers or client-wide steps, so add a Remove step for any banner that pollutes the text. Saving does not re-import: use Re-fetch source.',
};

// A URL document's own browser steps. Saved through their own super-admin route, never patchDoc:
// patchDoc turns a rejection into a toast and resolves, and a 400 here must keep the edits on screen
// next to the reason. Collapsed by default: steps are only needed for the rare page whose import
// comes back as banner text, so the card stays a one-line summary until a super admin opens it.
const DocumentBrowserStepsCard = ({ apiBase, docId, doc, scope, onSaved }) => {
  const fetch = useFetch();
  const [draft, setDraft] = useState(() => doc.browserSteps);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false);
  const count = Array.isArray(draft) ? draft.length : 0;
  const summary = `${count ? `${count} step${count === 1 ? '' : 's'}` : 'none'}${dirty ? ' · unsaved changes' : ''}`;

  const edit = (steps) => {
    setDraft(steps);
    setDirty(true);
    setError(null);
  };

  const save = () => {
    const invalid = validateBrowserSteps(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    const browserSteps = toBrowserStepsPayload(draft);
    setError(null);
    setSaving(true);
    fetch(`${apiBase}/documents/${docId}/browserSteps`, { method: 'PUT', body: JSON.stringify({ browserSteps }) })
      .then(({ document }) => {
        // Contract (confirmed on the API branch, re-check at merge): answers { document } like PUT
        // /status; without it, what was sent is what was stored.
        setDraft(document?.browserSteps ?? browserSteps);
        setDirty(false);
        if (document) onSaved({ ...document, browserSteps: document.browserSteps ?? browserSteps });
        notifications.show({
          title: 'Browser steps saved',
          message: 'They apply from the next import of this page — use Re-fetch source to import it now.',
          color: 'success',
        });
      })
      .catch((err) => setError(getErrorMessage(err, 'Could not save the browser steps')))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Group justify="space-between" wrap="nowrap">
        <Group gap={8} wrap="nowrap">
          <Text size="sm" fw={500}>
            Browser steps (this document)
          </Text>
          <Text fz={12} c="dimmed">
            {summary}
          </Text>
        </Group>
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          aria-expanded={opened}
          rightSection={opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          onClick={() => setOpened((value) => !value)}
        >
          {opened ? 'Hide' : 'Show'}
        </Button>
      </Group>
      <Collapse expanded={opened}>
        <Text fz={12} c="dimmed" mt={6} mb={10}>
          {HINTS[scope] || HINTS.domain}
        </Text>
        <BrowserStepsEditor steps={draft} onChange={edit} />
        {error && (
          <Text size="sm" c="danger" mt={8}>
            {error}
          </Text>
        )}
        <Group justify="flex-end" mt={12}>
          <Button
            size="sm"
            leftSection={<IconDeviceFloppy size={15} stroke={1.5} />}
            disabled={!dirty}
            loading={saving}
            onClick={save}
          >
            Save steps
          </Button>
        </Group>
      </Collapse>
    </>
  );
};

export default DocumentBrowserStepsCard;
